// Aba "Duplicadas" em /notas-fiscais — a VIGIA da fila por orcamento ([DA-489]).
// Lista venda que tem mais de UMA nota autorizada viva. Normalmente VAZIA: se
// aparecer linha aqui, saiu nota em dobro e o cliente/contador vai receber duas.
//
// A trava contra o problema ja esta de pe (emissao virou fila por orcamento). Esta
// tela nao previne nada — ela avisa se a trava vazou. A regra de quem entra na lista
// mora no backend (notas-fiscais.routes.ts, CTE NOTAS_AUTORIZADAS_VIVAS).
//
// "Ciente" nao conserta nem some com a linha: ela continua listada com a data da
// ciencia, mas sai da CONTAGEM do selo. E o que impede o contador de ficar travado
// nos 3 casos historicos (mes fiscal fechado, o Ruan decidiu nao mexer em 10/08/2026)
// e deixar o 4o passar batido. Reversivel pelo botao "Reabrir".
import { useCallback, useEffect, useState } from 'react';
import { Copy, Loader2, RefreshCw, Shield, Check, Undo2, ShieldCheck } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { formatMoeda, formatDataHoraSP } from '@fiodecobre/shared';

interface NotaDoGrupo {
  tipo: 'nfce' | 'nfe';
  id: number;
  numero: number;
  serie: string;
  chave: string | null;
  // numeric do Postgres: vem NUMBER dentro do json_agg e STRING quando e coluna de
  // primeiro nivel (como valor_duplicado). formatMoeda engole os dois.
  valor_total: string | number;
  emitido_em: string;
}

interface DuplicadaRow {
  orcamento_id: number;
  orcamento_numero: string | null;
  venda_id: number | null;
  venda_numero: string | null;
  cliente_nome: string | null;
  vendedor_nome: string | null;
  qtd: number;
  primeira_em: string;
  valor_duplicado: string | null;
  notas: NotaDoGrupo[];
  ciente_em: string | null;
  ciente_por: string | null;
}

export function DuplicadasTab({ onContagem }: { onContagem?: (n: number) => void }) {
  const toast = useToast();
  const [lista, setLista] = useState<DuplicadaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [marcando, setMarcando] = useState<number | null>(null);
  const [consultando, setConsultando] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<DuplicadaRow[]>('/api/notas-fiscais/duplicadas');
      setLista(data);
      onContagem?.(data.filter((r) => !r.ciente_em).length);
    } catch (e) {
      toast.erro((e as Error).message);
      setLista([]);
    } finally {
      setLoading(false);
    }
  }, [toast, onContagem]);

  useEffect(() => { void carregar(); }, [carregar]);

  async function alternarCiente(row: DuplicadaRow) {
    const jaCiente = !!row.ciente_em;
    if (!jaCiente) {
      const confirma = window.confirm(
        `Marcar como ciente a venda ${row.venda_numero ?? row.orcamento_numero ?? row.orcamento_id}?\n\n` +
        `A linha CONTINUA aqui, com a data da ciencia — so sai da contagem da aba.\n` +
        `Use quando voce ja resolveu (ou decidiu nao mexer) neste caso, pra que o\n` +
        `contador possa voltar a zero e a proxima nota em dobro grite.`
      );
      if (!confirma) return;
    }
    setMarcando(row.orcamento_id);
    try {
      if (jaCiente) {
        await api.delete(`/api/notas-fiscais/duplicadas/${row.orcamento_id}/ciente`);
        toast.sucesso('Ciencia desfeita — o caso volta a contar');
      } else {
        await api.post(`/api/notas-fiscais/duplicadas/${row.orcamento_id}/ciente`, {});
        toast.sucesso('Marcado como ciente — sai da contagem, continua listado');
      }
      void carregar();
    } catch (e) {
      toast.erro((e as Error).message || 'Erro ao gravar a ciencia');
    } finally {
      setMarcando(null);
    }
  }

  async function consultarSefaz(nota: NotaDoGrupo) {
    const chaveEstado = `${nota.tipo}-${nota.id}`;
    setConsultando(chaveEstado);
    try {
      const data = await api.post<{ cStat?: string; xMotivo?: string; status_sefaz?: string }>(
        `/api/${nota.tipo}/${nota.id}/consultar-sefaz`,
      );
      const msg = `Nota ${nota.numero} na SEFAZ: ${data.cStat ?? '?'} ${data.xMotivo ?? ''}`;
      if (data.status_sefaz === 'autorizada') toast.erro(msg); // autorizada AQUI e a ma noticia
      else toast.sucesso(msg);
      void carregar();
    } catch (e) {
      toast.erro((e as Error).message || 'Erro ao consultar SEFAZ');
    } finally {
      setConsultando(null);
    }
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
        <Loader2 size={20} className="animate-spin" style={{ display: 'inline-block' }} />
      </div>
    );
  }

  if (lista.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
        <div style={{ fontSize: 14, marginBottom: 4 }}>Nenhuma venda com nota em dobro.</div>
        <div style={{ fontSize: 12 }}>
          Esta aba deve ficar vazia. Se aparecer linha, a mesma venda tem mais de uma nota
          autorizada viva na SEFAZ — o cliente recebeu duas.
        </div>
      </div>
    );
  }

  const abertas = lista.filter((r) => !r.ciente_em).length;

  return (
    <div>
      <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--text-muted)' }}>
        {abertas > 0
          ? <strong style={{ color: '#b91c1c' }}>{abertas} {abertas === 1 ? 'venda' : 'vendas'} com nota em dobro</strong>
          : <>Nenhum caso aberto</>}
        {lista.length > abertas && <> • {lista.length - abertas} ja com ciencia</>}
        {' '}• historico completo, sem janela de data
        <button
          type="button"
          onClick={() => void carregar()}
          style={{ marginLeft: 12, background: 'transparent', border: 0, color: 'var(--primary)', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <RefreshCw size={12} /> Atualizar
        </button>
      </div>

      {/* overflow-x AUTO, nunca hidden — mesma razao da aba "Com problemas": a coluna
          de acoes clipa em tela estreita (tablet) e o que passa some sem barra. */}
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--table-header-bg)', textAlign: 'left' }}>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Quando</th>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Venda</th>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Cliente</th>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Vendedor</th>
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>Notas autorizadas</th>
              <th style={{ padding: '8px 10px', fontWeight: 600, textAlign: 'right' }}>Valor em dobro</th>
              <th style={{ padding: '8px 10px', fontWeight: 600, textAlign: 'right' }}>Acoes</th>
            </tr>
          </thead>
          <tbody>
            {lista.map((row) => {
              const ciente = !!row.ciente_em;
              return (
                <tr
                  key={row.orcamento_id}
                  style={{ borderTop: '1px solid var(--border)', opacity: ciente ? 0.6 : 1 }}
                >
                  <td style={{ padding: '8px 10px', fontSize: 12, whiteSpace: 'nowrap' }}>
                    {formatDataHoraSP(row.primeira_em)}
                  </td>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                    <div style={{ fontWeight: 600 }}>{row.venda_numero ?? '—'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{row.orcamento_numero ?? `#${row.orcamento_id}`}</div>
                  </td>
                  <td style={{ padding: '8px 10px' }}>{row.cliente_nome ?? '—'}</td>
                  <td style={{ padding: '8px 10px' }}>{row.vendedor_nome ?? '—'}</td>
                  <td style={{ padding: '8px 10px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {row.notas.map((n) => (
                        <div key={`${n.tipo}-${n.id}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{
                            background: n.tipo === 'nfce' ? 'rgba(37,99,235,0.12)' : 'rgba(167,139,250,0.18)',
                            color: n.tipo === 'nfce' ? '#1d4ed8' : '#6d28d9',
                            padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                            whiteSpace: 'nowrap',
                          }}>
                            {n.tipo === 'nfce' ? 'NFC-e' : 'NF-e'} #{n.numero}
                          </span>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                            {formatDataHoraSP(n.emitido_em)} • {formatMoeda(n.valor_total)}
                          </span>
                          <button
                            type="button"
                            onClick={() => void consultarSefaz(n)}
                            disabled={consultando === `${n.tipo}-${n.id}`}
                            title="Conferir na SEFAZ se esta nota continua autorizada (a SEFAZ so responde por chave — nao existe consulta de 'o que eu emiti')"
                            style={{
                              background: 'transparent', color: 'var(--primary)',
                              border: '1px solid var(--border)', padding: '1px 6px', borderRadius: 4,
                              fontSize: 11, cursor: 'pointer', display: 'inline-flex',
                              alignItems: 'center', gap: 3, whiteSpace: 'nowrap',
                              opacity: consultando === `${n.tipo}-${n.id}` ? 0.5 : 1,
                            }}
                          >
                            {consultando === `${n.tipo}-${n.id}`
                              ? <Loader2 size={10} className="animate-spin" />
                              : <Shield size={10} />}
                            SEFAZ
                          </button>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {row.valor_duplicado ? formatMoeda(row.valor_duplicado) : '—'}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {ciente && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <ShieldCheck size={12} />
                        Ciente em {formatDataHoraSP(row.ciente_em!)}
                        {row.ciente_por ? ` por ${row.ciente_por}` : ''}
                      </div>
                    )}
                    <div>
                      <button
                        type="button"
                        onClick={() => void alternarCiente(row)}
                        disabled={marcando === row.orcamento_id}
                        title={ciente
                          ? 'Desfazer a ciencia — o caso volta a contar na aba'
                          : 'Sai da contagem da aba, continua listado aqui com a data. Nao cancela nem conserta nota nenhuma.'}
                        style={{
                          background: ciente ? 'transparent' : '#fde68a',
                          color: ciente ? 'var(--text-muted)' : '#000',
                          border: ciente ? '1px solid var(--border)' : 0,
                          padding: '4px 10px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                          cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                          opacity: marcando === row.orcamento_id ? 0.5 : 1,
                        }}
                      >
                        {marcando === row.orcamento_id
                          ? <Loader2 size={12} className="animate-spin" />
                          : (ciente ? <Undo2 size={12} /> : <Check size={12} />)}
                        {ciente ? 'Reabrir' : 'Ciente'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <Copy size={12} style={{ marginTop: 2, flexShrink: 0 }} />
        <span>
          Reemissao apos erro NAO entra aqui — so conta nota <strong>autorizada e nao cancelada</strong>.
          Devolucao, remessa e retorno de consignacao tambem nao: elas reusam o numero do orcamento
          da nota original de proposito.
        </span>
      </div>
    </div>
  );
}
